/**
 * Prefill marketing flyer copy from fabric details + brand.
 * Image URL helpers match Kit Details page resolution order.
 */

function clip(value, fallback = '', max = 120) {
  const s = String(value ?? '').trim();
  if (!s) return fallback;
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function looksLikeFilename(value) {
  const s = String(value || '').trim();
  if (!s) return true;
  if (/\.(jpe?g|png|webp|gif|heic)$/i.test(s)) return true;
  if (/^whatsapp image/i.test(s)) return true;
  if (/^img[-_\s]?\d/i.test(s)) return true;
  if (/screenshot/i.test(s)) return true;
  return false;
}

function cleanName(value, fallback) {
  if (looksLikeFilename(value)) return fallback;
  return clip(value, fallback);
}

function formatGsm(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (/gsm/i.test(raw)) return raw;
  // Bare number → append GSM (e.g. analysis "220" or weight field)
  if (/^\d+(\.\d+)?$/.test(raw)) return `${raw} GSM`;
  return raw;
}

function formatWidth(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (/"|in\b|inch|cm|mm|m\b/i.test(raw)) return raw;
  if (/^\d+(\.\d+)?$/.test(raw)) return `${raw}"`;
  return raw;
}

function firstNonEmpty(...values) {
  for (const v of values) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  return '';
}

/**
 * Pull fabric catalog + analysis fields into editable LABEL / value rows
 * for the marketing poster detail panel.
 */
export function buildFabricDetailRows(fabricDetails, kitData) {
  const fabric = fabricDetails || kitData?.productDetails || {};
  const analysisRoot = fabric?.fabricAnalysis || {};
  const fa = analysisRoot.fabric_analysis || analysisRoot || {};
  const visual = analysisRoot.visual_characteristics || fa.visual_characteristics || {};
  const functional = analysisRoot.functional_properties || fa.functional_properties || {};

  const code = firstNonEmpty(
    fabric.code,
    fabric.sku,
    fabric.productCode,
    fabric.fabricCode,
    fabric.articleNumber,
    fabric.articleNo
  );
  const gsm = formatGsm(
    firstNonEmpty(fa.gsm_weight, fabric.fabricWeight, fabric.gsm, fabric.gsmWeight)
  );
  const width = formatWidth(
    firstNonEmpty(
      fabric.width,
      fabric.fabricWidth,
      fabric.specifications?.dimensions,
      fabric.specifications?.width
    )
  );
  const colors = []
    .concat(visual.primary_colors || [])
    .concat(visual.secondary_colors || [])
    .map((c) => String(c || '').trim())
    .filter(Boolean);
  const uniqueColors = [...new Set(colors)];

  const candidates = [
    { label: 'CODE', value: code },
    // NAME omitted — poster has no title; panel is property rows only.
    { label: 'TYPE', value: firstNonEmpty(fa.fabric_type) },
    { label: 'WEAVE', value: firstNonEmpty(fa.weave_or_knit) },
    { label: 'FIBER', value: firstNonEmpty(fa.fiber_guess, fa.composition) },
    { label: 'GSM', value: gsm },
    { label: 'WIDTH', value: width },
    { label: 'OPACITY', value: firstNonEmpty(fa.opacity, fabric.opacity) },
    { label: 'STRETCH', value: firstNonEmpty(fa.stretch, fabric.stretch) },
    { label: 'LOOK', value: firstNonEmpty(visual.visual_type) },
    { label: 'COLORS', value: uniqueColors.slice(0, 4).join(', ') },
    {
      label: 'SEASON',
      value: Array.isArray(functional.season_suitability)
        ? functional.season_suitability.filter(Boolean).slice(0, 3).join(', ')
        : firstNonEmpty(functional.season_suitability),
    },
  ];

  return candidates
    .filter((row) => row.value && !looksLikeFilename(row.value))
    .map((row) => ({
      label: row.label,
      // Full value — panel / export size to content (no ellipsis clip)
      value: String(row.value ?? '').trim(),
    }));
}

function buildFeatures(fa, fabric) {
  const features = [];
  if (fa?.fabric_type && !looksLikeFilename(fa.fabric_type)) {
    features.push(clip(fa.fabric_type, '', 40));
  }
  const gsm = formatGsm(fa?.gsm_weight || fabric?.fabricWeight);
  if (gsm) features.push(clip(gsm, '', 40));
  if (fa?.opacity) features.push(clip(fa.opacity, '', 40));
  if (fa?.stretch) features.push(clip(fa.stretch, '', 40));
  return features.slice(0, 4);
}

function firstHttpUrl(...candidates) {
  for (const c of candidates) {
    if (typeof c === 'string' && /^https?:\/\//i.test(c.trim())) return c.trim();
    if (typeof c === 'string' && c.trim().startsWith('data:')) return c.trim();
    if (typeof c === 'string' && c.trim().startsWith('/')) return c.trim();
  }
  return null;
}

/** Same resolution order as related-kit cards on Kit Details. */
export function resolveKitImageUrl(kit) {
  if (!kit) return null;
  return firstHttpUrl(
    kit.generatedImageUrl,
    kit.thumbnail,
    kit.watermarkedImages?.generatedImageUrl,
    kit.watermarkedImageUrl,
    kit.productDetails?.image,
    kit.productDetails?.originalImage,
    kit.image,
    kit.url
  );
}

/**
 * Same resolution order as the Kit Details fabric panel.
 * Prefer working display URLs (fabric.image) before optional s3 mirrors.
 */
export function resolveFabricImageUrl({
  fabricDetails,
  kitData,
  fabricImage,
  groupSourceImage,
} = {}) {
  const fabric = fabricDetails || kitData?.productDetails || {};
  return firstHttpUrl(
    fabricImage,
    fabric.image,
    fabric.images?.original?.imgbbUrl,
    fabric.images?.original?.url,
    fabric.images?.original?.s3Url,
    groupSourceImage,
    kitData?.sourceFabricImage,
    kitData?.productDetails?.image,
    kitData?.productDetails?.originalImage
  );
}

export function buildKitMarketingCopy({
  fabricDetails,
  kitData,
  brand,
  selectedKits = [],
} = {}) {
  const fabric = fabricDetails || kitData?.productDetails || {};
  const fa = fabric?.fabricAnalysis?.fabric_analysis || fabric?.fabricAnalysis || {};

  const productTitle = cleanName(
    fabric?.name || kitData?.productDetails?.name,
    'Premium Fabric'
  );

  // Never hardcode or echo FashDeck branding on customer marketing sheets.
  const rawBrand = brand?.name || fabric?.vendor?.name || fabric?.user?.name || '';
  const brandName = /fashdeck/i.test(String(rawBrand))
    ? ''
    : cleanName(rawBrand, '');

  const audienceRaw =
    fabric?.targetAudience ||
    kitData?.productDetails?.targetAudience ||
    selectedKits[0]?.productDetails?.targetAudience ||
    "Women's Wear";
  const audienceStr = typeof audienceRaw === 'string' ? audienceRaw.trim() : "Women's Wear";
  const audienceLine = /^for\s+/i.test(audienceStr) ? audienceStr : `For ${audienceStr}`;

  const footerText = '';

  const detailRows = buildFabricDetailRows(fabricDetails, kitData);

  return {
    brandName,
    brandSuffix: '',
    collectionLabel: '',
    productTitle: String(productTitle).toUpperCase(),
    audienceLine: clip(audienceLine, "For Women's Wear", 60),
    features: buildFeatures(fa, fabric),
    detailRows,
    detailStyle: {
      fontScale: 1,
      color: '#ffffff',
    },
    tagline: '',
    sealText: '',
    infoLines: [],
    heroQuote: '',
    perfectForTitle: '',
    perfectFor: [],
    footerText,
    kitLabels: [],
    swatchColors: [],
  };
}

export function patchKitMarketingCopy(copy, path, value) {
  if (!copy) return copy;
  if (path.startsWith('features.')) {
    const idx = Number(path.split('.')[1]);
    const features = [...(copy.features || [])];
    features[idx] = value;
    return { ...copy, features };
  }
  if (path.startsWith('detailRows.')) {
    const parts = path.split('.');
    const idx = Number(parts[1]);
    const field = parts[2]; // 'label' | 'value'
    const detailRows = [...(copy.detailRows || [])];
    const current = detailRows[idx] || { label: '', value: '' };
    detailRows[idx] = { ...current, [field]: value };
    return { ...copy, detailRows };
  }
  if (path.startsWith('detailStyle.')) {
    const field = path.split('.')[1];
    return {
      ...copy,
      detailStyle: {
        fontScale: 1,
        color: '#ffffff',
        ...(copy.detailStyle || {}),
        [field]: value,
      },
    };
  }
  return { ...copy, [path]: value };
}

export function removeKitMarketingDetailRow(copy, index) {
  if (!copy) return copy;
  const detailRows = [...(copy.detailRows || [])];
  if (index < 0 || index >= detailRows.length) return copy;
  detailRows.splice(index, 1);
  return { ...copy, detailRows };
}
